import React from 'react';
import { motion } from 'framer-motion';
import { COPY } from '../../contracts/copyMap';
import logoWide from '../../assets/logo-wide-t.png';
import MeshPortrait from './MeshPortrait';
import MobileIntro from './MobileIntro';
import FeatureHighlightStack from './FeatureHighlightStack';

// Desaturated fractal-noise grain (inline SVG) tiled over the hero so the
// near-black field reads as film, not flat ink. mix-blend-overlay keeps it to
// a faint tonal speckle.
// Shared easing for the intro → auth handover (DESIGN.md §7.1).
const EASE = [0.16, 1, 0.3, 1];

const GRAIN_URL =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='170' height='170'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E\")";

// Full-viewport marketing/auth hero (design-recreation, Sign In surface).
//
//   ┌────────────────────────────────────────────────┐
//   │      [mesh portrait backdrop, vignetted]       │
//   │  ┌────────────────┐          ┌──────────────┐  │
//   │  │ logo / tagline │          │   children   │  │
//   │  │ features/quote │          │ (auth panel) │  │
//   │  └────────────────┘          └──────────────┘  │
//   │  [footer prop — translucent strip over photo]  │
//   └────────────────────────────────────────────────┘
//
// The portrait carries the SAME aurum face mesh as the Create scan's
// analyzing state — but with PRE-BAKED landmarks (authHeroMesh.json,
// generated by scripts/bake-hero-mesh.py), so the sign-in screen never
// loads the MediaPipe wasm/model. FaceMeshOverlay's LANDMARK mode consumes
// container fractions and rescales via ResizeObserver, so the mesh tracks
// the face at every viewport size. The [MANIFESTO] censor bar renders as a
// crisp CSS overlay anchored at the baked eye-line.
//
// Desktop (lg+): absolutely centered backdrop behind the columns.
// Mobile: the portrait flows BETWEEN the marketing block and the auth card
// so it never sits behind the title or the sign-in UI.
//
// ── MOBILE: SPECIMEN FIRST, THEN AUTH ───────────────────────────────────────
// On a phone this surface was logo → tagline → login form, with the features,
// quote and portrait all hidden by CSS. 87% of sessions land here and 70.5%
// bounce: a visitor from cold search could not learn what the product does
// without creating an account first.
//
// So mobile now runs in two states. The specimen verdict owns the first screen
// and earns the tap; the auth panel replaces it on CTA.
//
//   [ logo / tagline ]            [ logo / tagline ]
//   [ SPECIMEN 78    ]   tap →    [ LOG IN | SIGN UP ]
//   [ SUBMIT PORTRAIT]  (fade)    [ email / password  ]
//   [ already a member? ]         [ ...               ]
//
// Two CTAs, deliberately: JOIN opens the sign-up tab, and a secondary link
// opens log-in. A single JOIN button would make returning members hunt, and
// returning direct visitors are a disproportionate share of the affiliate
// clicks the product actually earns.
//
// Desktop is UNCHANGED — it has room for the mesh portrait, the feature stack
// and the auth panel side by side, so it never enters the intro state.
//
// Props:
//   children:    ReactNode  right-column content (AuthPanel)
//   footer:      ReactNode  bottom strip rendered inside the viewport-height
//                           column so the translucent bg sits over the portrait
//   showIntro:   boolean    mobile only — specimen holds the screen
//   onEnterAuth: (mode) => void  dismiss the intro and select an auth tab
//
// pb-[60px] reserves the fixed PublicNav tab bar's height so the footer
// strip clears it on the sign-in surface.
const MarketingHero = ({ children, footer, showIntro = false, onEnterAuth = () => {} }) => (
  <div className="relative min-h-screen bg-obsidian flex flex-col overflow-hidden pb-[calc(60px+env(safe-area-inset-bottom))]">
    {/* Wide-desktop backdrop — only ≥1280px is there a free band between
        the 42% column and the 480px card for the face to sit unobscured.
        Large and pulled left of centre so it commands the field. Hidden on
        mobile and small desktops entirely. On the sign-up tab the mesh
        highlight follows the cursor and the portrait gets a subtle parallax. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 hidden min-[1280px]:block overflow-hidden"
    >
      <MeshPortrait
        style={{
          position: 'absolute',
          left: '43%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(54vw, 92vh)',
        }}
      />
    </div>

    {/* Film grain over the whole hero — breaks up the flat near-black. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-[1] opacity-[0.14] mix-blend-overlay"
      style={{ backgroundImage: GRAIN_URL, backgroundSize: '170px 170px' }}
    />

    {/* MOBILE SPLASH — rendered OUTSIDE the padded column so the portrait can
        bleed to both edges. The marketing column below carries px-8, which is
        why the splash cannot live inside it. */}
    {showIntro && <MobileIntro onEnterAuth={onEnterAuth} />}

    <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-stretch">
      {/* Left — marketing column. Centered below lg, left-aligned on desktop.
          NOT RENDERED while the mobile splash is up — not merely CSS-hidden.
          The splash carries its own <h1>, and a display:none copy still sits in
          the DOM where a crawler reads it, so hiding produced two identical h1s
          on the page that receives 87% of all sessions.
          Safe because showIntro is only ever true on mobile: a crawler (no JS,
          matchMedia defaulting false) gets showIntro=false and this column with
          its heading, exactly as before. */}
      {!showIntro && (
      <section className="w-full lg:w-[42%] lg:min-w-[460px] flex flex-col items-center text-center lg:items-start lg:text-left px-8 pt-10 pb-4 lg:pl-16 lg:pr-12 lg:pt-14 lg:pb-10">
        {/* The page that receives 87% of all sessions had no <h1> at all — the
            logo was a bare <img>. The visible mark is unchanged; the heading is
            what search and screen readers actually read. */}
        <h1 className="m-0 w-full">
          <span className="sr-only">{COPY.SIGN_IN_H1}</span>
          <motion.img
            src={logoWide}
            alt=""
            aria-hidden="true"
            className="block w-full max-w-[320px] lg:max-w-[460px] h-auto"
            variants={stagger}
            initial="hidden"
            animate="show"
            custom={0}
          />
        </h1>
        <motion.p
          className="font-display uppercase text-aurum font-medium m-0 mt-5 lg:mt-[26px]"
          style={{ fontSize: 15, letterSpacing: '0.24em', lineHeight: 1.9 }}
          variants={stagger}
          initial="hidden"
          animate="show"
          custom={1}
        >
          {COPY.MARKETING_TAGLINE_1}
          <br />
          {COPY.MARKETING_TAGLINE_2}
        </motion.p>
        <motion.div
          className="w-[34px] h-[2px] bg-aurum mt-4 lg:mt-[22px]"
          aria-hidden="true"
          variants={stagger}
          initial="hidden"
          animate="show"
          custom={2}
        />

        {/* Features + quote stay DESKTOP-ONLY. Measured: unhiding them on
            mobile pushed the email field below an 812px fold entirely. Desktop
            has the room; mobile does not, and the specimen explains the product
            better than three generic blurbs anyway. */}
        <FeatureHighlightStack className="hidden lg:flex mt-11" />
        <PullQuote />
      </section>
      )}

      {/* Right — auth panel. Hidden on mobile while the intro holds the screen;
          always present on desktop, which has room for both. */}
      <section
        className={[
          'flex-1 items-center justify-center lg:justify-end px-4 py-8 sm:px-8 lg:px-14 lg:py-10',
          showIntro ? 'hidden lg:flex' : 'flex',
        ].join(' ')}
      >
        {/* Animating the panel IN is what sells the handover — and it cannot
            strand anyone, because the intro is already gone by state. */}
        <motion.div
          className="w-full max-w-[480px]"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, ease: EASE }}
        >
          {children}
        </motion.div>
      </section>
    </div>

    {/* The footer strip is suppressed on mobile while the splash holds the
        screen — it rendered below the full-height splash and collided with the
        fixed PublicNav tab bar, so two sets of text overlapped. Desktop keeps
        it: there the strip sits over the portrait as designed. */}
    {footer && (
      <div className={showIntro ? 'hidden lg:block' : undefined}>{footer}</div>
    )}
  </div>
);

const stagger = {
  hidden: { opacity: 0, y: 14 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const PullQuote = () => (
  <motion.div
    className="hidden lg:block mt-[52px] pl-[18px] border-l border-aurum max-w-[280px]"
    variants={stagger}
    initial="hidden"
    animate="show"
    custom={7}
  >
    <p
      className="font-display italic text-[#B8B2A6] m-0"
      style={{ fontSize: 18, lineHeight: 1.5 }}
    >
      &ldquo;{COPY.MARKETING_PULL_QUOTE}&rdquo;
    </p>
    <p
      className="font-display text-aurum mt-3 m-0"
      style={{ fontSize: 12, letterSpacing: '0.22em' }}
    >
      &mdash; {COPY.MARKETING_PULL_QUOTE_ATTRIBUTION}
    </p>
  </motion.div>
);

export default MarketingHero;
